import apiClient, { handleApiError } from "@/api/openApiClient";
import { AdminUserSchema, AdminUserCreateRequest, AdminUserUpdateRequest } from "@telegraphio/bapi-types";

export interface AdminUsersApi {
  getAllUsers(): Promise<{ users: AdminUserSchema[] }>;
  getUser(userId: string): Promise<{ user: AdminUserSchema | null }>;
  createUser(requestData: AdminUserCreateRequest): Promise<{ user: AdminUserSchema | null }>;
  updateUser(userId: string, requestData: AdminUserUpdateRequest): Promise<{ user: AdminUserSchema | null }>;
  activateUser(userId: string): Promise<{ user: AdminUserSchema | null }>;
  disableUser(userId: string): Promise<{ user: AdminUserSchema | null }>;
  resetUserPassword(email: string): Promise<{ success: boolean; reset_token: boolean }>;
}

export const adminUsersApi: AdminUsersApi = {
  async getAllUsers() {
    const { data, error, response } = await apiClient.GET("/admin/users");

    if (error) {
      handleApiError(error, response, "Failed to fetch users");
    }

    return data;
  },

  async getUser(userId: string) {
    const { data, error, response } = await apiClient.GET("/admin/users/{user_id}", {
      params: {
        path: {
          user_id: userId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, `Failed to fetch user ${userId}`);
    }

    return data;
  },

  async createUser(requestData: AdminUserCreateRequest) {
    const { data, error, response } = await apiClient.POST("/admin/users", {
      body: {
        ...requestData,
      },
    });

    if (error) {
      handleApiError(error, response, "Failed to create user");
    }

    return data;
  },

  async updateUser(userId: string, requestData: AdminUserUpdateRequest) {
    const { data, error, response } = await apiClient.PUT("/admin/users/{user_id}", {
      params: {
        path: {
          user_id: userId,
        },
      },
      body: {
        ...requestData,
      },
    });

    if (error) {
      handleApiError(error, response, `Failed to update user ${userId}`);
    }

    return data;
  },

  async activateUser(userId: string) {
    const { data, error, response } = await apiClient.PATCH("/admin/users/{user_id}/enable", {
      params: {
        path: {
          user_id: userId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, `Failed to activate user ${userId}`);
    }

    return data;
  },

  async disableUser(userId: string) {
    const { data, error, response } = await apiClient.DELETE("/admin/users/{user_id}", {
      params: {
        path: {
          user_id: userId,
        },
      },
    });

    if (error) {
      handleApiError(error, response, `Failed to disable user ${userId}`);
    }

    return data;
  },

  async resetUserPassword(email: string) {
    const { data, error, response } = await apiClient.POST("/user/forgot", {
      body: { email },
    });

    if (error) {
      handleApiError(error, response, "Failed to reset password");
    }

    return data;
  },
};
